import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Input, Select, formatDisplayValue } from '../TowerEditor';
import { changeBody, summaryWeight } from '../../../modules/tower/towerWriteSlice';

import { getItemTotalWeight } from './weightSummary';

const CladdingForm = ({ cladding, size }) => {
    const dispatch = useDispatch();

    const [newCladding, setNewCladding] = useState(
        cladding || {
            siding: {},
            roofing: {},
        },
    );

    // Siding은 둘레 길이 (건축물 폭/길이에 600mm를 더해서 계산해야 한다)를 구해서 높이를 곱하면 면적이 쉽게 산출된다.
    // Roofing은 투영면적(건축물 폭/길이에 600mm를 더해서 계산해야 한다)을 cos(15)로 나눈다. 15도는 일반적인 루프 각도이다.
    // Siding Ratio는 최상층 즉, 높이를 1로 했을 때 사이딩이 설치되는 높이의 비율이다. 전체를 설치하면 1, 설치하지 않으면 0.

    const L = parseFloat(size?.main?.length || 0);
    const W = parseFloat(size?.main?.width || 0);
    const H = parseFloat(size?.main?.height || 0);

    const sectionPerimeter = 2 * (L + 0.6 + (W + 0.6));
    const sectionArea = (L + 0.6) * (W + 0.6);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const strArray = name.split('.');
        const category = strArray[0];
        const key = strArray[1];

        setNewCladding((prev) => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: value,
            },
        }));
    };

    const unitWeightMap = useRef({
        LC150x75x25: 8.26,
        'V115x0.6t': 8.3,
        'N/A': 0,
    });

    /* 단위 중량 산정 */
    useEffect(() => {
        setNewCladding((prev) => ({
            ...prev,
            roofing: {
                ...prev['roofing'],
                unitWeightPurlin: unitWeightMap.current[newCladding.roofing.purlinType],
                unitWeightCladding: unitWeightMap.current[newCladding.roofing.claddingType],
            },
            siding: {
                ...prev['siding'],
                unitWeightGirth: unitWeightMap.current[newCladding.siding.girthType],
                unitWeightCladding: unitWeightMap.current[newCladding.siding.claddingType],
            },
        }));
    }, [
        newCladding.roofing.claddingType,
        newCladding.roofing.purlinType,
        newCladding.siding.claddingType,
        newCladding.siding.girthType,
    ]);

    /* 내부 계산, 면적 */
    useEffect(() => {
        

        const areaRoofing = Math.ceil(
            ((sectionArea / Math.cos((15 * Math.PI) / 180)) * newCladding.roofing.ratio) / 100,
        );
        const weightPurlin = Math.ceil(areaRoofing * newCladding.roofing.unitWeightPurlin);
        const weightRoofing = Math.ceil(areaRoofing * newCladding.roofing.unitWeightCladding);

        const areaSiding = Math.ceil((sectionPerimeter * H * newCladding.siding.ratio) / 100);
        const weightGirth = Math.ceil(areaSiding * newCladding.siding.unitWeightGirth);
        const weightSiding = Math.ceil(areaSiding * newCladding.siding.unitWeightCladding);

        setNewCladding((prev) => ({
            ...prev,
            roofing: {
                ...prev['roofing'],
                area: areaRoofing,
                weightPurlin,
                weightCladding: weightRoofing,
                weight: weightPurlin + weightRoofing,
            },
            siding: {
                ...prev['siding'],
                area: areaSiding,
                weightGirth,
                weightCladding: weightSiding,
                weight: weightGirth + weightSiding,
            },
        }));
    }, [
        H,
        newCladding.roofing.claddingType,
        newCladding.roofing.purlinType,
        newCladding.roofing.ratio,
        newCladding.roofing.unitWeight,
        newCladding.roofing.unitWeightCladding,
        newCladding.roofing.unitWeightPurlin,
        newCladding.siding.claddingType,
        newCladding.siding.girthType,
        newCladding.siding.ratio,
        newCladding.siding.unitWeight,
        newCladding.siding.unitWeightCladding,
        newCladding.siding.unitWeightGirth,
        sectionArea,
        sectionPerimeter,
    ]);

    /* 리덕스에 weightSum 쓰기 */
    useEffect(() => {
        if (!cladding) {
            return;
        }
        dispatch(
            summaryWeight({
                key: 'cladding',
                value: getItemTotalWeight(cladding),
            }),
        );
    }, [cladding, dispatch]);

    /* 리덕스에 쓰기 */
    useEffect(() => {
        dispatch(
            changeBody({
                key: 'cladding',
                value: newCladding,
            }),
        );
    }, [dispatch, newCladding]);

    return (
        <>
            <tr>
                <td>Roofing</td>
                <td>Ratio</td>
                <td className="input-inside-td">
                    <Input name="roofing.ratio" value={newCladding.roofing.ratio} onChange={handleChange} />
                </td>
                <td>%</td>
            </tr>
            <tr>
                <td className="indentation">Purlin</td>
                <td colSpan={3} className="input-inside-td">
                    <Select name="roofing.purlinType" value={newCladding.roofing.purlinType} onChange={handleChange}>
                        <option value="" selected disabled hidden>
                            Select...
                        </option>
                        <option value="N/A">N/A</option>
                        <option value="LC150x75x25">LC150x75x25x3.2</option>
                    </Select>
                </td>
                <td className="right-align">{newCladding.roofing.purlinType !== 'N/A' && formatDisplayValue(newCladding.roofing.area)}</td>
                <td>m2</td>
                <td className="right-align">{formatDisplayValue(newCladding.roofing.unitWeightPurlin)}</td>
                <td className="right-align">{formatDisplayValue(newCladding.roofing.weightPurlin)}</td>
            </tr>
            <tr>
                <td className="indentation">Cladding</td>
                <td colSpan={3} className="input-inside-td">
                    <Select
                        name="roofing.claddingType"
                        value={newCladding.roofing.claddingType}
                        onChange={handleChange}
                    >
                        <option value="" selected disabled hidden>
                            Select...
                        </option>
                        <option value="N/A">N/A</option>
                        <option value="V115x0.6t">V-115 0.6t</option>
                    </Select>
                </td>
                <td className="right-align">{newCladding.roofing.claddingType !== 'N/A' && formatDisplayValue(newCladding.roofing.area)}</td>
                <td>m2</td>
                <td className="right-align">{formatDisplayValue(newCladding.roofing.unitWeightCladding)}</td>
                <td className="right-align">{formatDisplayValue(newCladding.roofing.weightCladding)}</td>
            </tr>
            <tr>
                <td className="spacer"></td>
            </tr>
            {/* SIDING */}
            <tr>
                <td>Siding</td>
                <td>Ratio</td>
                <td className="input-inside-td">
                    <Input name="siding.ratio" value={newCladding.siding.ratio} onChange={handleChange} />
                </td>
                <td>%</td>
            </tr>
            <tr>
                <td className="indentation">Girth</td>
                <td colSpan={3} className="input-inside-td">
                    <Select name="siding.girthType" value={newCladding.siding.girthType} onChange={handleChange}>
                        <option value="" selected disabled hidden>
                            Select...
                        </option>
                        <option value="N/A">N/A</option>
                        <option value="LC150x75x25">LC150x75x25x3.2</option>
                    </Select>
                </td>
                <td className="right-align">{(newCladding.siding.girthType !== 'N/A') && formatDisplayValue(newCladding.siding.area)}</td>
                <td>m2</td>
                <td className="right-align">{formatDisplayValue(newCladding.siding.unitWeightGirth)}</td>
                <td className="right-align">{formatDisplayValue(newCladding.siding.weightGirth)}</td>
            </tr>
            <tr>
                <td className="indentation">Cladding</td>
                <td colSpan={3} className="input-inside-td">
                    <Select name="siding.claddingType" value={newCladding.siding.claddingType} onChange={handleChange}>
                        <option value="" selected disabled hidden>
                            Select...
                        </option>
                        <option value="N/A">N/A</option>
                        <option value="V115x0.6t">V-115 0.6t</option>
                    </Select>
                </td>
                <td className="right-align">{(newCladding.siding.claddingType !== 'N/A') && formatDisplayValue(newCladding.siding.area)}</td>
                <td>m2</td>
                <td className="right-align">{formatDisplayValue(newCladding.siding.unitWeightCladding)}</td>
                <td className="right-align">{formatDisplayValue(newCladding.siding.weightCladding)}</td>
            </tr>
            <tr>
                <td className="spacer"></td>
            </tr>
        </>
    );
};

export default CladdingForm;
